import React, {FC, useState, useEffect, useRef} from "react"
import moment from "moment"
import PropTypes from "prop-types"

import {ReduxState} from "@irati/store/types"
import Flex from "@irati/ui/src/Flex"
import FormattedMessage, {getMessage, useIntl} from "@irati/utils/lang"
import {connect} from "@irati/store"
import withBranding, {WithBranding} from "@irati/brand/withBranding"
import {Body, H3} from "@irati/ui/src/Typography"
import Button from "@irati/ui/src/Button"
import Validation from "@irati/utils/validation"
import Form from "@irati/ui/src/Form"
import magicLoginService from "@irati/auth/services/magicLogin"
import Loading from "@irati/ui/src/Loading"
import {CreateMagicLoginResponse, MagicLogin, MagicLoginResponse, MagicLoginStatus} from "@irati/types"
import {getErrorMessage, getErrorValues} from "@irati/utils/lang/formatError"
import authentication from "@irati/auth"
import ErrorText from "@irati/ui/src/ErrorText"
import {Input} from "@irati/ui/src/FormItem"
import loginService from "@irati/auth/services/login"
import History from "@irati/utils/history"
import getTenantFromPath from "@irati/identity/helpers/getTenantFromPath"
import Alert, {AlertTypes} from "@irati/ui/src/Alert"
import session from "@irati/auth/services/session"
import tokens from "@irati/auth/services/tokens"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import tracking from "@irati-tracking/platform/platform"

import {REDIRECT_STORAGE_KEY} from "../../constants/login"
import {BusinessLogo} from "./styled"
import {ScreenTabs} from "."

let TRY_AGAIN_SECONDS: number
let MAX_CHAR_LENGTH: number

interface Props extends WithBranding {
  email: string
  isMobile: boolean
  login: CreateMagicLoginResponse
  loginWithPhone: boolean
  goBack: () => void
  onResend: () => void
  onMultipleLogins: (logins: MagicLogin[]) => void
  onRedirect: (tenantLogin: MagicLogin) => void
  onTabChange: (screen: string) => void
}

interface Context {
  apps: {[key: string]: string}
}

const ConfirmCode: FC<Props> = (
  {login, email, branding, isMobile, loginWithPhone, goBack, onResend, onMultipleLogins, onRedirect, onTabChange},
  {apps}: Context
) => {
  const [code, setCode] = useState("")
  const [load, setLoad] = useState<Promise<any> | null>(null)
  const [timer, setTimer] = useState<number>()
  const [errorMsg, setErrorMsg] = useState("")
  const [showAlert, setShowAlert] = useState<boolean>()
  const [adminLoginAlert, setAdminLoginAlert] = useState(false)

  const {formatMessage} = useIntl()
  const inputRef = useRef<HTMLInputElement>()
  const now = moment()

  const schema = () =>
    Validation.string(formatMessage({id: "platform.defaultValidation.required"})).matches(
      /^\d{6}$/,
      formatMessage({id: "platform.defaultValidation.fixedLength"}, {count: MAX_CHAR_LENGTH})
    )

  const handleOnInputChange = (value: string) => {
    setErrorMsg("")
    setShowAlert(false)
    setAdminLoginAlert(false)

    if (value && value.length <= MAX_CHAR_LENGTH) {
      setCode(value)
      if (value.length === MAX_CHAR_LENGTH) {
        magicLogin(value)
      }
    } else if (!value) {
      setCode("")
    }
  }

  const handleSetError = (errorMessage: string) => {
    tracking.loginOTPError(loginWithPhone ? "otp_phone" : "otp")
    tracking.loginErrorDetails(errorMessage)
    setErrorMsg(errorMessage)
    setShowAlert(true)
  }

  const magicLogin = (value: string) => {
    tracking.loginOTPSubmit(loginWithPhone ? "otp_phone" : "otp")
    const loaded = magicLoginService
      .magicLogin(login.uuid, email, value, getTenantFromPath() ? authentication.unAuthedBusiness().uuid : undefined)
      .then((res: MagicLoginResponse) => {
        if (res.logins.length === 0) {
          handleSetError(
            formatMessage({
              id: loginWithPhone
                ? "platform.oneTimePassword.error.phoneNumberDoesNotExist"
                : "platform.oneTimePassword.error.emailDoesNotExist"
            })
          )
        } else {
          handleOnLogin(res.logins)
        }
      })
      .catch((err: PBError) => {
        const message = getMessage(formatMessage, getErrorMessage(err), getErrorValues(err))
        handleSetError(message)
      })
    setLoad(loaded)
  }

  const handleOnLogin = (logins: MagicLogin[]) => {
    return logins.length === 1 ? handleLoginStatus(logins[0]) : onMultipleLogins(logins)
  }

  const handleLoginStatus = (tenantLogin: MagicLogin) => {
    switch (tenantLogin.status) {
      case MagicLoginStatus.MagicLoginStatusSuccess:
        return handleOTPLogin(tenantLogin?.auth?.legacyToken, tenantLogin?.business?.subdomain)
      case MagicLoginStatus.MagicLoginStatusPasswordLoginDisabled:
        return onRedirect(tenantLogin)
      case MagicLoginStatus.MagicLoginStatusUserDisabled:
        return handleSetError(formatMessage({id: "platform.oneTimePassword.error.userDeactivated"}))
      case MagicLoginStatus.MagicLoginStatusBusinessDisabled:
        return handleSetError(formatMessage({id: "platform.oneTimePassword.error.businessDeactivated"}))
      case MagicLoginStatus.MagicLoginStatusAdminPhoneLoginDisabled:
        return handleAdminAlert()
      default:
        return handleSetError(formatMessage({id: "platform.oneTimePassword.error.unknown"}))
    }
  }

  const handleAdminAlert = () => {
    setAdminLoginAlert(true)
    tracking.loginOTPError("Admin cannnot login")
  }

  const handleOTPLogin = (accessToken?: string, subdomain?: string) => {
    if (accessToken && subdomain) {
      localStorage.setItem("login_detail", loginWithPhone ? "otp_phone phone_number_otp_login" : "otp email_otp_login")
      if (getTenantFromPath()) {
        oneTimePasswordLogin(accessToken)
      } else {
        redirectToSubdomain(accessToken, subdomain)
      }
    } else {
      const unknownError = formatMessage({id: "platform.oneTimePassword.error.unknown"})
      handleSetError(unknownError)
    }
  }

  const redirectToSubdomain = (accessToken: string, subdomain: string) => {
    tokens.set({accessToken})
    return session.postCrossDomain(subdomain, {accessToken}, {pathname: "/welcome/login/code"})
  }

  const oneTimePasswordLogin = (accessToken: string) => {
    const loaded = loginService
      .oneTimePasswordLogin(accessToken)
      .then(() => {
        let nextPathname = sessionStorage.getItem(REDIRECT_STORAGE_KEY) || ""

        // HACK: there is an odd admin bug where during logout, the logout url
        // gets persisted, so users are just bounced back out on login until they
        // start a new browser sessions
        if (nextPathname === "/welcome/logout") {
          nextPathname = ""
          sessionStorage.removeItem(REDIRECT_STORAGE_KEY)
        }

        if (nextPathname) {
          return History.replace(nextPathname)
        }
        const isAdmin = !!authentication.apps().admin
        if (isAdmin && !isMobile) {
          return History.replace({pathname: "/"}, apps.ADMIN)
        } else {
          History.replace({pathname: "/"})
        }
      })
      .catch((err: PBError) => {
        const message = getMessage(formatMessage, getErrorMessage(err), getErrorValues(err))
        handleSetError(message)
      })
    setLoad(loaded)
  }

  const handleResendCode = () => {
    tracking.loginOTPResend(loginWithPhone ? "otp_phone" : "otp")
    setCode("")
    setErrorMsg("")
    setShowAlert(false)
    setAdminLoginAlert(false)
    onResend()
  }

  const setCountDown = () => {
    let countDown = TRY_AGAIN_SECONDS
    const timerId = setInterval(() => {
      if (countDown === 0) {
        clearTimeout(timerId)
      } else {
        countDown = countDown - 1
        setTimer(countDown)
      }
    }, 1000)
  }

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.maxLength = 6
    }
  }, [inputRef])

  useEffect(() => {
    if (login && login.digits && login.resend?.seconds) {
      const resend = moment.unix(parseInt(login.resend?.seconds, 10))
      const duration = moment.duration(resend.diff(now))

      TRY_AGAIN_SECONDS = Math.ceil(duration.asSeconds())
      MAX_CHAR_LENGTH = login.digits

      setTimer(TRY_AGAIN_SECONDS)
      setShowAlert(true)
      setCountDown()
    }
  }, [login])

  const title = loginWithPhone ? "platform.oneTimePassword.otpCode.alertTitle2" : "platform.oneTimePassword.otpCode.alertTitle"
  const subTitle = loginWithPhone ? "platform.oneTimePassword.otpCode.subtitle2" : "platform.oneTimePassword.otpCode.subtitle"

  return (
    <>
      <Flex justifyContent="flex-start" marginBottom={2}>
        <Button small tertiary onClick={goBack}>
          <Icon icon={IconTypes.angleLeft} small marginLeft={0.5} />
          <FormattedMessage id="platform.back" />
        </Button>
      </Flex>
      <Flex direction="column" alignItems="center" justifyContent="center">
        {showAlert && (
          <Alert
            nonDismissable={errorMsg === ""}
            type={errorMsg !== "" ? AlertTypes.error : AlertTypes.success}
            title={errorMsg !== "" ? errorMsg : title}
            subTitle={errorMsg !== "" ? "errors.magic.PBMagicUnexpected" : subTitle}
            onHide={() => setShowAlert(false)}
          />
        )}
        {adminLoginAlert && (
          <Alert
            type={AlertTypes.error}
            title="platform.oneTimePassword.error.adminLoginPhoneDisabled"
            link="platform.oneTimePassword.error.adminLoginPhoneDisabledLink"
            onClickLink={() => onTabChange(ScreenTabs.email)}
            onHide={() => setAdminLoginAlert(false)}
          />
        )}
        {branding?.businessLogoUrl && <BusinessLogo src={formatMessage({id: branding?.businessLogoUrl})} />}
        <H3 marginBottom={0.5}>
          <FormattedMessage id="platform.oneTimePassword.enterEmail.title" />
        </H3>
        <Body small weight="light" marginBottom={2} maxWidth="100%" textAlign="center">
          <FormattedMessage
            id="platform.oneTimePassword.otpCode.subTitle"
            values={{
              digits: MAX_CHAR_LENGTH,
              email: (
                <Body small weight="semibold" display="inline">
                  {email}
                </Body>
              ),
              back: (
                <Button tertiary onClick={goBack} marginLeft>
                  <Flex alignItems="center">
                    <FormattedMessage id="platform.oneTimePassword.otpCode.back" />
                    <Icon icon={IconTypes.chevronRight} size={12} marginLeft={0.5} />
                  </Flex>
                </Button>
              )
            }}
          />
        </Body>
        <Flex direction="column" width="100%">
          <Loading load={load} />
          <Form>
            <Input
              flush
              name="otp-code"
              type="number"
              label={formatMessage({id: "platform.oneTimePassword.otpCode.inputLabel"})}
              placeholder={formatMessage({id: "platform.oneTimePassword.otpCode.inputPlaceholder"})}
              value={code}
              validation={schema()}
              onChange={handleOnInputChange}
              inputRef={(t) => (inputRef.current = t)}
            />
            {errorMsg && <ErrorText>{errorMsg}</ErrorText>}
            {timer && timer > 0 ? (
              <Body small weight="light" textAlign="center" marginBottom marginTop={2}>
                <FormattedMessage
                  id="platform.oneTimePassword.otpCode.tryAgain"
                  values={{timer: moment.utc(timer * 1000).format("mm:ss")}}
                />
              </Body>
            ) : (
              <Flex gap={1} justifyContent="center" marginTop={2} marginBottom>
                <Body small weight="light">
                  <FormattedMessage id="platform.oneTimePassword.otpCode.codeNotReceived" />
                </Body>
                <Button tertiary onClick={handleResendCode}>
                  <Flex alignItems="center">
                    <FormattedMessage id="platform.oneTimePassword.otpCode.resendBtton" />
                    <Icon icon={IconTypes.chevronRight} size={12} marginLeft={0.5} />
                  </Flex>
                </Button>
              </Flex>
            )}
          </Form>
        </Flex>
      </Flex>
    </>
  )
}

const mapStateToProps = ({mq}: ReduxState) => {
  return {
    isMobile: mq.lessThan.medium
  }
}

ConfirmCode.contextTypes = {
  apps: PropTypes.object.isRequired
}

export default connect(mapStateToProps)(withBranding(ConfirmCode))
